import { IconArrowLeft, IconCalendar, IconCheck, IconChevronRight, IconMapPin, IconSparkles, IconStar, IconUsers } from "@tabler/icons-react";
import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type PreContract, type Proposal, type ProposalItem, type ProposalOption, type ProposalQuestion, type ProposalView } from "../api";
import { kindIcon } from "../components/kindIcon";
import { Card, Empty, ErrorNote, SectionTitle, Spinner } from "../components/ui";
import { formatDate, formatDateRange, formatDateTime, formatMoney, formatNumber } from "../format";
import type { Key } from "../i18n";
import { navigate } from "../router";
import { useSession } from "../session";

/** Set before leaving an accepted proposal; the trip page shows the one-time welcome and clears it. */
export const WELCOME_STORAGE = "welcomeTrip";

type Failure = { key: Key; body?: Key; signIn?: boolean };

/** `/p/:id`: the proposal is the moment of truth, a brand page rather than a PDF (docs/16 §2.2, §2.7). */
export function ProposalPage({ id }: { id: string }) {
  const { brand, lang, t } = useSession();
  const [view, setView] = useState<ProposalView | undefined>(undefined);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState<"choose" | "accept" | "decline" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  // The pre-contract form of the chosen option: read and ticked before a package can be accepted and paid.
  const [form, setForm] = useState<PreContract | null>(null);
  const [formRead, setFormRead] = useState(false);
  const chosenId = view ? chosenOf(view.proposal)?.id : undefined;
  const isOpen = view ? view.proposal.status !== "accepted" && view.proposal.status !== "declined" : false;
  useEffect(() => {
    if (!chosenId || !isOpen) return;
    setForm(null);
    api
      .preContract(id, chosenId)
      .then((f) => {
        setForm(f);
        setFormRead(f.required && !!f.accepted);
      })
      .catch(() => setForm(null));
  }, [id, chosenId, isOpen, tick]);

  useEffect(() => {
    setFailure(null);
    api
      .proposal(id)
      .then(setView)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) setFailure({ key: "proposalSignIn", signIn: true });
        else if (err instanceof ApiError && err.code === "not_yours") setFailure({ key: "proposalNotYours", signIn: true });
        else if (err instanceof ApiError && err.status === 404) setFailure({ key: "notFoundTitle" });
        else if (err instanceof ApiError && (err.code === "proposal_expired" || err.status === 410)) setFailure({ key: "proposalExpired", body: "proposalExpiredBody" });
        else setFailure({ key: "somethingWrong" });
      });
  }, [id, tick]);

  const messageFor = (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.code === "proposal_closed") return t("proposalClosed");
      if (err.code === "proposal_expired") return t("proposalExpired");
      if (err.code === "pre_contract_required") return t("preContractRequired");
      if (err.code === "pre_contract_changed") return t("preContractChanged");
    }
    return t("somethingWrong");
  };

  const update = (proposal: Proposal) => setView((prev) => (prev ? { ...prev, proposal } : prev));

  const choose = async (optionId: string) => {
    if (!view || view.proposal.chosenOptionId === optionId || busy) return;
    setBusy("choose");
    setActionError(null);
    try {
      update((await api.chooseOption(id, optionId)).proposal);
    } catch (err) {
      setActionError(messageFor(err));
    } finally {
      setBusy(null);
    }
  };

  const accept = async () => {
    if (!view || busy) return;
    if (form?.required && !formRead) {
      setActionError(t("preContractRequired"));
      return;
    }
    setBusy("accept");
    setActionError(null);
    try {
      if (form?.required && !form.accepted) await api.acceptPreContract(id, form.optionId, form.sha256);
      const res = await api.acceptProposal(id, chosenOf(view.proposal)?.id);
      try {
        sessionStorage.setItem(WELCOME_STORAGE, res.trip.id);
      } catch {
        // Storage blocked: the trip page simply shows no welcome banner.
      }
      navigate(`/trips/${res.trip.id}`);
    } catch (err) {
      setActionError(messageFor(err));
      // A proposal changed under the page: fetch it and its form again.
      if (err instanceof ApiError && err.code === "pre_contract_changed") setTick((n) => n + 1);
      setBusy(null);
    }
  };

  const decline = async () => {
    if (!view || busy) return;
    setBusy("decline");
    setActionError(null);
    try {
      update((await api.declineProposal(id, declineReason.trim() || undefined)).proposal);
      setDeclining(false);
    } catch (err) {
      setActionError(messageFor(err));
    } finally {
      setBusy(null);
    }
  };

  if (failure) {
    return (
      <main className="page">
        <a href="/" className="link-button back-link">
          <IconArrowLeft size={16} /> {t("back")}
        </a>
        <Empty title={t(failure.key)} body={failure.body ? t(failure.body) : undefined} />
        {failure.signIn ? (
          <a href="/signin" className="button button-primary">
            {t("signIn")}
          </a>
        ) : (
          failure.key === "somethingWrong" && (
            <button type="button" className="button" onClick={() => setTick((n) => n + 1)}>
              {t("retry")}
            </button>
          )
        )}
      </main>
    );
  }
  if (!view) {
    return (
      <main className="page">
        <Spinner />
      </main>
    );
  }

  const { proposal, questions } = view;
  const open = proposal.status === "sent" || proposal.status === "viewed" || proposal.status === "option_chosen";
  const chosen = chosenOf(proposal);
  const consultant = proposal.consultant;
  const travellersLabel = proposal.travellers === 1 ? t("traveller") : t("travellers", { n: proposal.travellers });
  const depositPercent = proposal.paymentTerms.depositPercent ?? (proposal.paymentTerms.depositMinor ? undefined : 30);
  const depositMinor = chosen ? (proposal.paymentTerms.depositMinor ?? (depositPercent !== undefined ? Math.round((chosen.priceMinor * depositPercent) / 100) : undefined)) : undefined;

  return (
    <main className="page proposal">
      <a href="/" className="link-button back-link">
        <IconArrowLeft size={16} /> {t("back")}
      </a>

      <header className={`proposal-cover ${proposal.coverUrl ? "has-image" : ""}`} style={proposal.coverUrl ? { backgroundImage: `url("${proposal.coverUrl}")` } : undefined}>
        <div className="proposal-cover-inner">
          <span className="eyebrow">{t("proposalEyebrow")}</span>
          <h1>{proposal.title}</h1>
          <p className="proposal-meta">
            {proposal.destination && (
              <span>
                <IconMapPin size={16} /> {proposal.destination}
              </span>
            )}
            {proposal.startsOn && (
              <span>
                <IconCalendar size={16} /> {formatDateRange(proposal.startsOn, proposal.endsOn, lang)}
              </span>
            )}
            <span>
              <IconUsers size={16} /> {travellersLabel}
            </span>
          </p>
          {proposal.validUntil && open && <span className="proposal-valid">{t("validUntil", { date: formatDate(proposal.validUntil.slice(0, 10), lang) })}</span>}
        </div>
      </header>

      {consultant && (
        <Card className="consultant-card">
          <div className="consultant-head">
            {consultant.photoUrl ? <img src={consultant.photoUrl} alt="" className="avatar avatar-photo" /> : <span className="avatar avatar-initials">{initials(consultant.name)}</span>}
            <div>
              <span className="eyebrow">{t("yourConsultant")}</span>
              <p>{t("preparedBy", { name: consultant.name })}</p>
              {consultant.bio && <p className="muted small">{consultant.bio}</p>}
            </div>
          </div>
        </Card>
      )}

      {proposal.status === "accepted" && (
        <div className="notice">
          <IconCheck size={18} />
          <div className="notice-text">
            <strong>{t("proposalAccepted")}</strong>
            <span className="small">{t("proposalAcceptedBody")}</span>
          </div>
          {proposal.tripId && (
            <a href={`/trips/${proposal.tripId}`} className="button button-small button-primary">
              {t("viewTrip")}
            </a>
          )}
        </div>
      )}
      {(proposal.status === "declined" || proposal.status === "expired") && (
        <Empty title={t(proposal.status === "declined" ? "proposalDeclined" : "proposalExpired")} body={t(proposal.status === "declined" ? "proposalDeclinedBody" : "proposalExpiredBody")} />
      )}

      {proposal.summary && (
        <>
          <SectionTitle>{t("aboutTrip")}</SectionTitle>
          <Card>
            <p className="proposal-summary">{proposal.summary}</p>
          </Card>
        </>
      )}

      <SectionTitle>{t("options")}</SectionTitle>
      <div className="stack options" role="radiogroup" aria-label={t("options")}>
        {proposal.options.map((option) => (
          <OptionCard key={option.id} option={option} currency={proposal.currency} selected={chosen?.id === option.id} disabled={!open || busy !== null} onSelect={() => void choose(option.id)} />
        ))}
      </div>

      <SectionTitle>{t("dayByDay")}</SectionTitle>
      <DayByDay proposal={proposal} option={chosen} />

      {chosen && (
        <Card className="total-card">
          <div className="total-row">
            <span className="eyebrow">{t("yourTotal")}</span>
            <span className="total-amount">{formatMoney(chosen.priceMinor, proposal.currency, lang)}</span>
          </div>
          <p className="small total-points">
            <IconSparkles size={16} /> {t("earnsPoints", { points: formatNumber(proposal.pointsToEarn ?? Math.floor(chosen.priceMinor / 100), lang), programme: proposal.loyaltyProgramme ?? brand?.loyaltyProgramme ?? "" })}
          </p>
          <p className="muted small">
            {depositMinor !== undefined ? t("depositLine", { deposit: formatMoney(depositMinor, proposal.currency, lang), balance: formatMoney(chosen.priceMinor - depositMinor, proposal.currency, lang) }) : t("depositTerms")}
          </p>
          {open && (
            <>
              {form?.required && <PreContractBox form={form} read={formRead} onRead={setFormRead} />}
              {actionError && <ErrorNote>{actionError}</ErrorNote>}
              <button type="button" className="button button-primary button-block" disabled={busy !== null || (form?.required === true && !formRead)} onClick={() => void accept()}>
                {busy === "accept" ? t("accepting") : t("acceptPay")}
              </button>
              {declining ? (
                <div className="decline-box">
                  <p className="small">{t("declineConfirm")}</p>
                  <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder={t("declineReason")} rows={2} maxLength={500} />
                  <div className="row-buttons">
                    <button type="button" className="button button-small" disabled={busy !== null} onClick={() => void decline()}>
                      {busy === "decline" ? t("accepting") : t("confirmDecline")}
                    </button>
                    <button type="button" className="link-button" onClick={() => setDeclining(false)}>
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="link-button decline-link" disabled={busy !== null} onClick={() => setDeclining(true)}>
                  {t("decline")}
                </button>
              )}
            </>
          )}
        </Card>
      )}

      <SectionTitle>{t("questions")}</SectionTitle>
      <Questions proposalId={id} questions={questions} open={open} onAsked={(q) => setView((prev) => (prev ? { ...prev, questions: [...prev.questions, q] } : prev))} />
    </main>
  );
}

function chosenOf(proposal: Proposal): ProposalOption | undefined {
  return proposal.options.find((o) => o.id === proposal.chosenOptionId) ?? proposal.options.find((o) => o.recommended) ?? proposal.options[0];
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

function OptionCard({ option, currency, selected, disabled, onSelect }: { option: ProposalOption; currency: string; selected: boolean; disabled: boolean; onSelect: () => void }) {
  const { lang, t } = useSession();
  return (
    <div className={`card option-card ${selected ? "selected" : ""}`} role="radio" aria-checked={selected} tabIndex={0} onClick={disabled ? undefined : onSelect} onKeyDown={(e) => {
      if (!disabled && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onSelect();
      }
    }}>
      <div className="option-head">
        <span className={`radio ${selected ? "on" : ""}`} aria-hidden="true">
          {selected && <IconCheck size={14} />}
        </span>
        <div className="list-text">
          <div className="option-title-row">
            <strong>{option.title}</strong>
            {option.recommended && (
              <span className="chip chip-recommended">
                <IconStar size={12} /> {t("recommended")}
              </span>
            )}
          </div>
          {option.description && <span className="muted small">{option.description}</span>}
        </div>
        <span className="option-price">{formatMoney(option.priceMinor, currency, lang)}</span>
      </div>
      {option.items.length > 0 && (
        <ul className="list option-items">
          {option.items.map((item, i) => (
            <li key={i} className="list-row option-item">
              <span className="list-icon list-icon-small">{kindIcon(item.kind, 16)}</span>
              <span className="list-text">
                <span>{item.title}</span>
                {item.detail && <span className="muted small">{item.detail}</span>}
              </span>
              {item.day !== undefined && <span className="muted small">{t("dayN", { n: item.day + 1 })}</span>}
            </li>
          ))}
        </ul>
      )}
      {!disabled && !selected && (
        <span className="option-select muted small">
          {t("selectOption")} <IconChevronRight size={14} />
        </span>
      )}
      {selected && <span className="option-select small chosen-label">{t("chosen")}</span>}
    </div>
  );
}

/** Adds `days` to a calendar date. */
function addDays(iso: string, days: number) {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function DayByDay({ proposal, option }: { proposal: Proposal; option: ProposalOption | undefined }) {
  const { lang, t } = useSession();
  const days = new Map<number, { story?: { title: string; text: string }; items: ProposalItem[] }>();
  for (const s of proposal.story) days.set(s.day, { story: { title: s.title, text: s.text }, items: [] });
  const undated: ProposalItem[] = [];
  for (const item of option?.items ?? []) {
    if (item.day === undefined) {
      undated.push(item);
      continue;
    }
    const entry = days.get(item.day) ?? { items: [] };
    entry.items.push(item);
    days.set(item.day, entry);
  }
  const keys = [...days.keys()].sort((a, b) => a - b);
  if (keys.length === 0 && undated.length === 0) return <Empty title={t("timelineEmpty")} />;
  return (
    <>
      {keys.map((day) => {
        const entry = days.get(day)!;
        const date = proposal.startsOn ? formatDate(addDays(proposal.startsOn, day), lang, { weekday: "long", day: "numeric", month: "long" }) : "";
        return (
          <section key={day} className="day">
            <h3 className="day-heading">
              {t("dayN", { n: day + 1 })}
              {date ? ` · ${date}` : ""}
            </h3>
            <Card className="story-card">
              {entry.story && (
                <div className="story-text">
                  <strong>{entry.story.title}</strong>
                  <p className="small">{entry.story.text}</p>
                </div>
              )}
              {entry.items.length > 0 && (
                <ul className="list">
                  {entry.items.map((item, i) => (
                    <li key={i} className="list-row">
                      <span className="list-icon">{kindIcon(item.kind)}</span>
                      <span className="list-text">
                        <strong>{item.title}</strong>
                        <span className="muted small">
                          {[item.start, item.end].filter(Boolean).join(" – ")}
                          {item.detail ? (item.start ? ` · ${item.detail}` : item.detail) : ""}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </section>
        );
      })}
      {undated.length > 0 && (
        <Card>
          <ul className="list">
            {undated.map((item, i) => (
              <li key={i} className="list-row">
                <span className="list-icon">{kindIcon(item.kind)}</span>
                <span className="list-text">
                  <strong>{item.title}</strong>
                  {item.detail && <span className="muted small">{item.detail}</span>}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function Questions({ proposalId, questions, open, onAsked }: { proposalId: string; questions: ProposalQuestion[]; open: boolean; onAsked: (q: ProposalQuestion) => void }) {
  const { lang, t } = useSession();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (value.length < 2 || busy) return;
    setBusy(true);
    setError(null);
    try {
      onAsked((await api.askQuestion(proposalId, value)).question);
      setText("");
    } catch {
      setError(t("somethingWrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="questions-card">
      {questions.length === 0 && <p className="muted small">{t("questionsEmpty")}</p>}
      {questions.map((q) => (
        <div key={q.id} className="question">
          <p className="question-text">{q.text}</p>
          <span className="muted small">{formatDateTime(q.askedAt, lang)}</span>
          {q.answer ? (
            <div className="answer">
              <span className="eyebrow">{q.answeredBy ?? t("yourConsultant")}</span>
              <p className="small">{q.answer}</p>
            </div>
          ) : (
            <p className="muted small answer-soon">{t("answerSoon")}</p>
          )}
        </div>
      ))}
      {open && (
        <form className="form ask-form" onSubmit={(e) => void submit(e)}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("askPlaceholder")} rows={2} maxLength={2000} />
          {error && <ErrorNote>{error}</ErrorNote>}
          <button type="submit" className="button button-small" disabled={busy || text.trim().length < 2}>
            {busy ? t("sending") : t("ask")}
          </button>
        </form>
      )}
    </Card>
  );
}

/** The pre-contract information form (Directive (EU) 2015/2302) as the server rendered it, and the tick that accepts it. */
function PreContractBox({ form, read, onRead }: { form: Extract<PreContract, { required: true }>; read: boolean; onRead: (v: boolean) => void }) {
  const { t } = useSession();
  const [shown, setShown] = useState(false);
  return (
    <div className="pre-contract">
      <p className="eyebrow">{t("preContractTitle")}</p>
      <p className="small">{t("preContractLead")}</p>
      <button type="button" className="link-button" onClick={() => setShown((v) => !v)} aria-expanded={shown}>
        {t("preContractOpen")}
      </button>
      {shown && <iframe className="pre-contract-frame" title={t("preContractTitle")} sandbox="" srcDoc={form.html} />}
      <label className="check-row">
        <input type="checkbox" checked={read} onChange={(e) => onRead(e.target.checked)} />
        <span className="small">{t("preContractCheck")}</span>
      </label>
    </div>
  );
}
